import { FormField, FieldType, Validatable, useUIActions, useUIState, useUIStateActions, ModalId, StaticValue, ValidationModal2, BooleanField, PriceField, RadioField, option, useFormKeyboardNavigation } from "../../../../features/ui"
import { useEffect, useMemo, useState } from "react"
import { dedupe, defined, firstItem, toList } from "../../../../utils/listUtils"
import { round } from "../../../../utils/typeUtils"
import { useActions } from "./actions"
import { TableName } from '../../../../tables'
import { Customer, HoardOrder, SaleOperation } from '../../state/types'
import { useHoardHooks } from '../../hooks/hoardHooks'
import { Data2, DataTypeForm2, useDataTypesHooks } from '../../../../features/data-types'
import { CashFundType, PaymentCategory, useCashFundLoaders, usePaymentTypeHooks, usePaymentTypeLoaders } from '../../../administration'
import { operationHelpers, usePriceOutlineHooks } from '../../../operations'
import { useNavigate } from 'react-router-dom'
import { SalePaymentItemsTable } from '../sale-operation/sale-payment-items-table/salePaymentItemsTable'
import { SalePaymentItemFormModal } from '../sale-operation/sale-payment-item-form-modal/salePaymentItemFormModal'
import { SalePaymentItemData } from '../../services/sale-operation-service/types'
import { FiscalDocument, FiscalDocumentStatus, FiscalDocumentType } from '../../../fiscal-documents'
import { Emitter, Event } from '../../../../features/event-emitter'
import { PendingFinancialDelivery } from '../../services/hoardService'
import { useSalesHooks } from '../../hooks/salesHooks'
import { DEFAULT_CHANGE_MULTIPLE } from '../../constants/payments'
import styles from './confirmSalePaymentForm.module.css'

type ChangeOption = 'up' | 'down' | 'exact' | 'custom'

// "Agregar" button of the payment items table
const ADD_PAYMENT_ITEM_SELECTOR = '#salePaymentItems thead button'

type Props = {
    table: TableName
    operation: SaleOperation
    customer: Customer
    fiscalDocuments?: FiscalDocument[]
    increaseAmount?: number
    pendingDelivery?: PendingFinancialDelivery
}

export const ConfirmSalePaymentForm = (props: Props) => {
    const { table, operation, customer, fiscalDocuments = [], increaseAmount, pendingDelivery } = props
    // A negative credit in favor (left by a previous partial payment bug) isn't a debt: it's ignored
    const creditInFavor = Math.max(0, customer.creditInFavor || 0)

    const { openedModal } = useUIState()

    const [salePaymentItems, setSalePaymentItems] = useState<SalePaymentItemData[]>([])
    const [currentSalePaymentItem, setCurrentSalePaymentItem] = useState<SalePaymentItemData>()
    const [pendingAmountValidation, setPendingAmountValidation] = useState<string>()
    const [roundingRemovedWarning, setRoundingRemovedWarning] = useState<boolean>(false)
    const [giveChange, setGiveChange] = useState(false)
    const [changeAmount, setChangeAmount] = useState<number | undefined>()
    // How the change amount was chosen: rounded up, rounded down, exact, or typed by the user ("Otro monto")
    const [changeMode, setChangeMode] = useState<ChangeOption>('exact')
    const [giveChangeRemovedWarning, setGiveChangeRemovedWarning] = useState(false)
    const [checkFormKey, setCheckFormKey] = useState(0)
    const [paymentStatusChanged, setPaymentStatusChanged] = useState<boolean>(false)
    const [pendingPaymentData, setPendingPaymentData] = useState<Data2>()

    const { describeCode } = operationHelpers
    const { getAdjustedTotal } = usePriceOutlineHooks()
    const { getPaymentTypeYield, isCreditAccount } = usePaymentTypeHooks()
    const { getIncreasedHoard } = useHoardHooks()
    const { formatValue } = useDataTypesHooks()
    const { getSettings } = useSalesHooks()
    const changeMultiple = (getSettings()?.changeMultiple as number | undefined) || DEFAULT_CHANGE_MULTIPLE
    const navigate = useNavigate()

    const paymentTypeLoaders = usePaymentTypeLoaders()
    const cashFundLoaders = useCashFundLoaders()

    const { validatePaymentStatus, submitPayment } = useActions()
    const { openModal, closeModal } = useUIActions()
    const { setModalValidations } = useUIStateActions()

    // Keyboard: the form starts on "Agregar" (a payment item), Tab stays inside the form and Enter goes to the next field
    const { containerRef: keyboardContainerRef, onKeyDown: onFormKeyDown, focusAfter } = useFormKeyboardNavigation({
        autoFocusSelector: ADD_PAYMENT_ITEM_SELECTOR
    })

    const isSale = useMemo(() => table === TableName.SALE_ORDERS, [])
    const codeLabel = useMemo(() => describeCode(operation), [operation])

    useEffect(() => setCheckFormKey(checkFormKey + 1), [openedModal])

    const paymentStatus = useMemo(() => operation.paymentStatus, [])

    // Con una ampliación pendiente, el monto a pagar se calcula con el acopio ya ampliado
    const operationToPay = useMemo(() => increaseAmount ?
        getIncreasedHoard(operation as HoardOrder, increaseAmount) :
        operation
    , [operation, increaseAmount])

    // Acopio en cuenta corriente: se cobra ahora solo la ampliación (con las condiciones del acopio, con cualquier
    // medio de pago) y el resto sigue en la cuenta corriente
    const collectIncreaseOnly = useMemo(() => !!increaseAmount && isCreditAccount(operation.paymentTypeId), [])

    // Si la operación ya tiene comprobantes emitidos, el recargo del comprobante quedó congelado al
    // emitirse. Cobrar con otro recargo no bloquea, pero se advierte para evitar inconsistencias.
    // La factura anulada (o con anulación en curso) y su NC de anulación no cuentan: al re-facturar
    // con el medio corregido el aviso ya no debe aparecer por el comprobante viejo.
    const hasIssuedDocuments = useMemo(() => fiscalDocuments.some(document => {
        const isIssued = document.status === FiscalDocumentStatus.TO_AUTHORIZE ||
            document.status === FiscalDocumentStatus.AUTHORIZED
        if (!isIssued) {
            return false
        }
        if (document.documentType === FiscalDocumentType.INVOICE) {
            return !document.annulledAt && !document.annulmentInFlight
        }
        // La NC de anulación compensa a la factura vieja; la NC manual sí cuenta (aviso conservador).
        return document.documentType === FiscalDocumentType.CREDIT_NOTE &&
            !document.annulmentOfFiscalDocumentId
    }), [fiscalDocuments])

    const selectedPaymentTypeId = useMemo(
        () => firstItem(salePaymentItems.filter(item => !item.creditInFavor))?.paymentTypeId || operation.paymentTypeId,
        [salePaymentItems, operation.paymentTypeId]
    )

    // El recargo del comprobante queda congelado al emitirse: si después se cobra con otro medio, la
    // operación recalcula su total con el recargo nuevo y deja de cerrar contra la factura. No se bloquea,
    // pero se advierte para que anulen el comprobante si hace falta rehacerlo. Aplica a ventas y acopios.
    const paymentTypeYieldChanged = useMemo(
        () => !collectIncreaseOnly &&
            hasIssuedDocuments &&
            round(getPaymentTypeYield(selectedPaymentTypeId)) !== round(operation.paymentTypeYield || 0),
        [collectIncreaseOnly, hasIssuedDocuments, selectedPaymentTypeId, operation.paymentTypeYield]
    )

    const yieldWarningMessage = useMemo(() => {
        const issuedPaymentTypeName = paymentTypeLoaders().findPaymentType(operation.paymentTypeId)?.name || 'el medio de pago original'
        return `${isSale ? 'Esta venta' : 'Este acopio'} ya posee facturas emitidas con medio de pago ${issuedPaymentTypeName} (${operation.paymentTypeYield || 0}%). Utilizar un medio distinto dejará el comprobante con el recargo anterior. Si necesitás rehacerlo, anulá la factura desde Documentos y volvé a facturar.`
    }, [isSale, operation.paymentTypeId, operation.paymentTypeYield])

    const basePaymentTypes = useMemo(() => paymentTypeLoaders().findAllPaymentTypes()
        .filter(paymentType => paymentType.category === PaymentCategory.CHECK || paymentType.cashFundIds.length > 0)
    , [])

    const paymentTypes = useMemo(() => {
        if (collectIncreaseOnly) {
            return basePaymentTypes
        }
        // With adjustment notes (frozen amounts) only payment types with the operation yield are allowed, so the notes keep matching the total
        if (operation.adjustedAmount) {
            return basePaymentTypes.filter(paymentType => (paymentType.yield || 0) === (operation.paymentTypeYield || 0))
        }
        const existingPaymentTypeId = operation.paymentItems.find(paymentItem => !paymentItem.creditInFavor)?.paymentTypeId
        const referencePaymentType = basePaymentTypes.find(paymentType => paymentType.id === (existingPaymentTypeId || firstItem(salePaymentItems)?.paymentTypeId))
        return basePaymentTypes.filter(paymentType => !referencePaymentType || paymentType.yield === referencePaymentType.yield)
    }, [basePaymentTypes, salePaymentItems, currentSalePaymentItem])

    const totalAmounts = useMemo(() => {
        const { findPaymentType } = paymentTypeLoaders()
        const operationPaymentType = findPaymentType(operation.paymentTypeId)
        const amounts: [string, number][] =
            dedupe(basePaymentTypes.concat(operationPaymentType ? [operationPaymentType] : []))
            .map(paymentType => {
                if (collectIncreaseOnly) {
                    return [paymentType.id, increaseAmount!]
                }
                const paymentTypeId = operation.paymentTypeId !== paymentType.id ? paymentType.id : undefined
                return [paymentType.id, getAdjustedTotal(table, operationToPay, paymentTypeId) - operation.paidAmount]
            })
        return new Map<string, number>(amounts)
    }, basePaymentTypes)

    const totalAmount = useMemo(() => {
        const paymentTypeId = firstItem(salePaymentItems)?.paymentTypeId || operation.paymentTypeId
        return totalAmounts.get(paymentTypeId || operation.paymentTypeId)
    }, [salePaymentItems, totalAmounts, operation.paymentTypeId])

    const getPendingAmount = (totalAmountParam: number) => totalAmountParam - creditInFavor - salePaymentItems.reduce((sum, salePaymentItem) => sum + salePaymentItem.amount, 0)

    // Raw pending before any change adjustment (can be negative when overpaid)
    const pendingAmountRaw = useMemo(() => getPendingAmount(totalAmount || 0), [totalAmount, creditInFavor, salePaymentItems])

    // Effective pending: when giveChange is active, change offsets the credit back into pending
    const pendingAmount = useMemo(() => {
        if (giveChange && changeAmount !== undefined) return pendingAmountRaw + changeAmount
        return pendingAmountRaw
    }, [pendingAmountRaw, giveChange, changeAmount])

    // Cubre el pago también cuando el crédito iguala el total: si no, no se puede agregar un medio (el saldo ya es $0)
    // ni confirmar sin medio de pago
    const creditCoversPayment = useMemo(
        () => totalAmount !== undefined && creditInFavor >= totalAmount,
        [creditInFavor, totalAmount]
    )
    // Al confirmar se usa el crédito a favor del cliente hasta cubrir el monto a pagar
    const creditApplied = useMemo(() => Math.max(0, Math.min(creditInFavor, totalAmount || 0)), [creditInFavor, totalAmount])

    const pendingAmounts = useMemo(() => {
        const pendingAmountsMap = new Map<string, number>()
        totalAmounts.forEach((amount, paymentTypeId) => {
            const raw = getPendingAmount(amount)
            pendingAmountsMap.set(paymentTypeId, giveChange && changeAmount !== undefined ? raw + changeAmount : raw)
        })
        return pendingAmountsMap
    }, [totalAmounts, creditInFavor, salePaymentItems, giveChange, changeAmount])

    const nonRoundingItems = useMemo(() => salePaymentItems.filter(item => !item.isRounding), [salePaymentItems])
    const roundingItem = useMemo(() => salePaymentItems.find(item => item.isRounding), [salePaymentItems])
    const isRoundingEnabled = !!roundingItem

    const nonRoundingPaidAmount = useMemo(() => nonRoundingItems.reduce((sum, item) => sum + item.amount, 0), [nonRoundingItems])
    const pendingWithoutRounding = useMemo(() => {
        const raw = (totalAmount || 0) - creditInFavor - nonRoundingPaidAmount
        if (giveChange && changeAmount !== undefined) return raw + changeAmount
        return raw
    }, [totalAmount, creditInFavor, nonRoundingPaidAmount, giveChange, changeAmount])

    const allAllowRounding = useMemo(() => {
        if (nonRoundingItems.length === 0) return false
        const { findPaymentType } = paymentTypeLoaders()
        return nonRoundingItems.every(item => {
            const pt = findPaymentType(item.paymentTypeId)
            return pt?.roundingEnabled && (pt.maxRoundingAmount || 0) > 0
        })
    }, [nonRoundingItems])

    const minMaxRoundingAmount = useMemo(() => {
        if (!allAllowRounding || nonRoundingItems.length === 0) return 0
        const { findPaymentType } = paymentTypeLoaders()
        return Math.min(...nonRoundingItems.map(item => findPaymentType(item.paymentTypeId)?.maxRoundingAmount || 0))
    }, [nonRoundingItems, allAllowRounding])

    const effectiveRoundingCap = useMemo(() => round(Math.min(pendingWithoutRounding, minMaxRoundingAmount)), [pendingWithoutRounding, minMaxRoundingAmount])

    const canRound = allAllowRounding && effectiveRoundingCap > 0

    // Change can only be given from cash (not from cards, transfers or checks)
    const isCashItem = (item: SalePaymentItemData) => {
        if (item.isRounding || item.creditInFavor || !item.cashFundId) return false
        const paymentType = paymentTypeLoaders().findPaymentType(item.paymentTypeId)
        const cashFund = cashFundLoaders().findCashFund(item.cashFundId)
        return paymentType?.category === PaymentCategory.CASH_FUND && cashFund?.type === CashFundType.CASH
    }

    const cashReceived = useMemo(() => round(salePaymentItems.filter(isCashItem).reduce((sum, item) => sum + item.amount, 0)), [salePaymentItems])

    // The rounding is only reduced when it no longer fits (the user can always lower it manually).
    // While giving change it isn't adjusted: it's validated when confirming
    useEffect(() => {
        if (!roundingItem || giveChange) return
        if (!allAllowRounding) {
            setSalePaymentItems(current => current.filter(item => !item.isRounding))
            setRoundingRemovedWarning(true)
            return
        }
        if (effectiveRoundingCap <= 0) {
            setSalePaymentItems(current => current.filter(item => !item.isRounding))
            return
        }
        if (roundingItem.amount > effectiveRoundingCap) {
            setSalePaymentItems(current => current.map(item =>
                item.isRounding ? { ...item, amount: effectiveRoundingCap } : item
            ))
        }
    }, [roundingItem, allAllowRounding, effectiveRoundingCap])

    // What the customer paid above what is owed (without the rounding, which is ours)
    const overpaidAmount = useMemo(() => round(nonRoundingPaidAmount + creditInFavor - (totalAmount || 0)), [nonRoundingPaidAmount, creditInFavor, totalAmount])

    // Change in bills: rounded up to the change multiple (we round in favor of the customer, covering the difference
    // with the payment type rounding) or down (the difference goes to the customer credit in favor), or exact
    const changeOptions = useMemo(() => {
        if (overpaidAmount <= 0) return undefined
        const roundUp = round(Math.ceil(round(overpaidAmount / changeMultiple)) * changeMultiple)
        const roundDown = round(Math.floor(round(overpaidAmount / changeMultiple)) * changeMultiple)
        const roundingNeeded = round(roundUp - overpaidAmount)
        const roundUpBlockReason = roundingNeeded <= 0 ? undefined :
            !allAllowRounding ? 'Algún medio de pago cargado no permite redondeo' :
            roundingNeeded > minMaxRoundingAmount ? `El redondeo máximo es ${formatValue(minMaxRoundingAmount, FieldType.PRICE)}` :
            roundUp > cashReceived ? 'El vuelto superaría el efectivo recibido' :
            undefined
        return {
            exact: overpaidAmount,
            roundUp,
            roundDown,
            roundingNeeded,
            canRoundUp: roundingNeeded > 0 && !roundUpBlockReason,
            roundUpBlockReason,
            creditLeft: round(overpaidAmount - roundDown)
        }
    }, [overpaidAmount, changeMultiple, allAllowRounding, minMaxRoundingAmount, cashReceived])

    const applyChangeOption = (choice: ChangeOption) => {
        setChangeMode(choice)
        // "Otro monto": the amount is typed by the user, starting from the current one
        if (choice === 'custom' || !changeOptions) return
        const { roundUp, roundDown, exact, roundingNeeded } = changeOptions
        const withoutRounding = (items: SalePaymentItemData[]) => items.filter(item => !item.isRounding)
        if (choice === 'up') {
            setChangeAmount(roundUp)
            const roundingItem: SalePaymentItemData = { index: 'rounding', isRounding: true, amount: roundingNeeded }
            setSalePaymentItems(current => sortItems([...withoutRounding(current), roundingItem]))
        } else {
            setChangeAmount(choice === 'down' ? roundDown : exact)
            setSalePaymentItems(current => withoutRounding(current))
        }
    }

    // In favor of the customer: rounded up when the rounding allows it, otherwise down (the rest to credit in favor)
    const getSuggestedChangeOption = (): ChangeOption => {
        if (!changeOptions || changeOptions.roundingNeeded <= 0) return 'exact'
        if (changeOptions.canRoundUp) return 'up'
        return changeOptions.roundDown > 0 ? 'down' : 'exact'
    }

    // Auto-deactivate giveChange when the overpayment disappears; when the payments change, the change is suggested
    // again (unless the user typed another amount)
    useEffect(() => {
        if (!giveChange) return
        if (overpaidAmount <= 0) {
            setGiveChange(false)
            setChangeAmount(undefined)
            setGiveChangeRemovedWarning(true)
            setSalePaymentItems(current => current.filter(item => !item.isRounding))
            return
        }
        if (changeMode !== 'custom') {
            applyChangeOption(getSuggestedChangeOption())
        }
    }, [overpaidAmount])

    // "Otro monto": if the typed change leaves a pending amount, it's rounded automatically. If the payment types
    // don't allow that rounding, it's said when confirming
    useEffect(() => {
        if (!giveChange || changeMode !== 'custom') return
        const pending = round(-overpaidAmount + (changeAmount || 0))
        const withoutRounding = (items: SalePaymentItemData[]) => items.filter(item => !item.isRounding)
        if (pending > 0) {
            const roundingItem: SalePaymentItemData = { index: 'rounding', isRounding: true, amount: pending }
            setSalePaymentItems(current => sortItems([...withoutRounding(current), roundingItem]))
        } else {
            setSalePaymentItems(current => withoutRounding(current))
        }
    }, [changeAmount, changeMode])

    const sortItems = (items: Partial<SalePaymentItemData>[]) => {
        return items
            .sort((a, b) => {
                if (a.isRounding) return 1
                if (b.isRounding) return -1
                return a.paymentTypeId!.localeCompare(b.paymentTypeId!)
            })
            .map((salePaymentItem, index) => ({
                ...salePaymentItem,
                index: index.toString()
            })) as SalePaymentItemData[]
    }

    const onAddPaymentItem = () => {
        setCurrentSalePaymentItem(undefined)
        setPendingAmountValidation(undefined)
        setRoundingRemovedWarning(false)
        setGiveChangeRemovedWarning(false)
        openModal(ModalId.SALE_PAYMENT_ITEM_FORM)
    }

    const onEditPaymentItem = (index: string) => {
        setPendingAmountValidation(undefined)
        setRoundingRemovedWarning(false)
        setGiveChangeRemovedWarning(false)
        setCurrentSalePaymentItem(salePaymentItems.find(item => item.index === index))
        openModal(ModalId.SALE_PAYMENT_ITEM_FORM)
    }

    const onRemovePaymentItem = (index: string) => {
        setPendingAmountValidation(undefined)
        setRoundingRemovedWarning(false)
        setGiveChangeRemovedWarning(false)
        const newSalePaymentItems = salePaymentItems.filter(item => item.index !== index)
        setSalePaymentItems(sortItems(newSalePaymentItems))
    }

    const onToggleRounding = (value?: boolean) => {
        setRoundingRemovedWarning(false)
        if (value) {
            if (effectiveRoundingCap <= 0) return
            const newItem: SalePaymentItemData = {
                index: 'rounding',
                isRounding: true,
                amount: effectiveRoundingCap
            }
            setSalePaymentItems(current => sortItems([...current, newItem]))
        } else {
            setSalePaymentItems(current => current.filter(item => !item.isRounding))
        }
    }

    const onChangeRoundingAmount = (value?: number) => {
        setSalePaymentItems(current => current.map(item => item.isRounding ? { ...item, amount: value || 0 } : item))
    }

    const onToggleGiveChange = (value?: boolean) => {
        setGiveChangeRemovedWarning(false)
        if (value) {
            setGiveChange(true)
            applyChangeOption(getSuggestedChangeOption())
        } else {
            setGiveChange(false)
            setChangeAmount(undefined)
            setSalePaymentItems(current => current.filter(item => !item.isRounding))
        }
    }

    // Not clamped here: the field clamps to min on blur, and validate() covers the rest
    const onChangeChange = (value?: number) => setChangeAmount(value)

    const fields: FormField[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: isSale ? 'Venta' : 'Acopio',
            render: () => <>{codeLabel}</>
        },
        {
            name: 'paymentDate',
            type: FieldType.DATE,
            label: 'Fecha de Cobro',
            defaultValue: new Date(),
            max: new Date()
        },
        {
            name: 'totalAmount',
            type: FieldType.PRICE,
            label: 'Monto a Pagar',
            loading: !totalAmount,
            render: () => <>{formatValue(totalAmount, FieldType.PRICE)}</>
        },
        {
            name: 'salePaymentItems',
            type: FieldType.TABLE,
            label: 'Medios de Pago',
            render: () => (
                <SalePaymentItemsTable
                    salePaymentItems={salePaymentItems}
                    disableCreate={pendingAmount <= 0 || paymentTypes.length === 0}
                    createTooltip={creditInFavor > 0 && creditCoversPayment ? ['El pago está cubierto', 'por el crédito a favor'] : undefined}
                    onCreate={onAddPaymentItem}
                    onEditRow={onEditPaymentItem}
                    onRemoveRow={onRemovePaymentItem}
                    roundingMaxAmount={effectiveRoundingCap}
                    onChangeRoundingAmount={onChangeRoundingAmount}
                    createMode
                />
            )
        },
        {
            name: 'pendingAmount',
            type: FieldType.PRICE,
            label: pendingAmount >= 0 ? 'Saldo Pendiente' : 'Crédito a Favor',
            render: () => (
                <Validatable validations={defined([
                    pendingAmountValidation,
                    roundingRemovedWarning ? 'El redondeo se quitó porque un medio de pago no lo permite' : undefined,
                    giveChangeRemovedWarning ? 'El vuelto se quitó porque ya no hay excedente a devolver' : undefined
                ])} left>
                    <StaticValue type={FieldType.PRICE} value={Math.abs(pendingAmount)} />
                </Validatable>
            )
        }
    ]
    if (increaseAmount) {
        fields.splice(1, 0, {
            name: 'increaseAmount',
            type: FieldType.PRICE,
            label: 'Ampliación',
            render: () => <>
                {formatValue(increaseAmount, FieldType.PRICE)}
                <small className="text-muted ms-2">
                    {pendingDelivery ? 'Por una entrega que supera el crédito del acopio: la entrega se emite al confirmar el cobro' : 'Se guarda al confirmar el cobro'}
                </small>
            </>
        })
    }
    if (creditApplied > 0) {
        const remainingCredit = creditInFavor - creditApplied
        const creditFields: FormField[] = [
            {
                name: 'credit',
                type: FieldType.PRICE,
                label: 'Crédito a Favor',
                render: () => <>
                    - {formatValue(creditApplied, FieldType.PRICE)}
                    <small className="text-muted ms-2">
                        {remainingCredit > 0 ?
                            `Se descuenta del monto a pagar (el cliente tiene ${formatValue(creditInFavor, FieldType.PRICE)}, le quedan ${formatValue(remainingCredit, FieldType.PRICE)})` :
                            'Se descuenta del monto a pagar'}
                    </small>
                </>
            },
            {
                name: 'amountToCollect',
                type: FieldType.PRICE,
                label: 'Monto a Cobrar',
                render: () => <>{formatValue((totalAmount || 0) - creditApplied, FieldType.PRICE)}</>
            }
        ]
        const totalAmountIndex = fields.findIndex(field => field.name === 'totalAmount')
        fields.splice(totalAmountIndex + 1, 0, ...creditFields)
    }
    // Change is only offered when there's an overpayment and cash to take it from
    if (giveChange || (pendingAmountRaw < 0 && cashReceived > 0)) {
        fields.push({
            name: 'giveChange',
            type: FieldType.BOOLEAN,
            label: 'Dar Vuelto',
            render: () => (
                <BooleanField
                    id="giveChangeEnabled"
                    label="Dar vuelto en efectivo"
                    value={giveChange}
                    optional
                    onChange={onToggleGiveChange}
                />
            )
        })
    }
    // Rounded options only when the overpaid amount isn't already a multiple of the change multiple
    const changeRadioOptions = changeOptions && changeOptions.roundingNeeded > 0 ?
        [
            option('up', ''),
            ...(changeOptions.roundDown > 0 ? [option('down', '')] : []),
            option('exact', ''),
            option('custom', '')
        ] :
        [option('exact', ''), option('custom', '')]

    // The change amount in bold; what happens with the difference, in normal weight
    const renderChangeOptionLabel = ({ value }: { value: string }) => {
        if (!changeOptions) return null
        const price = (amount: number) => formatValue(amount, FieldType.PRICE)
        switch (value as ChangeOption) {
            case 'up':
                return <>
                    <b>{price(changeOptions.roundUp)}</b> · redondeo {price(changeOptions.roundingNeeded)}
                    {changeOptions.roundUpBlockReason && <span className={styles.optionNote}> ({changeOptions.roundUpBlockReason})</span>}
                </>
            case 'down':
                return <><b>{price(changeOptions.roundDown)}</b> · {price(changeOptions.creditLeft)} a crédito a favor</>
            case 'exact':
                return <><b>{price(changeOptions.exact)}</b> · exacto</>
            case 'custom':
                return <>Otro monto</>
        }
    }

    if (giveChange) {
        fields.push({
            name: 'vuelto',
            type: FieldType.PRICE,
            label: 'Vuelto',
            // The quick amounts go first; choosing one fills the field below, where any other amount can be typed
            render: () => (
                <div className={styles.changeField}>
                    {changeOptions && (
                        <RadioField
                            id="changeOption"
                            value={changeMode}
                            options={changeRadioOptions}
                            disabledOptions={changeOptions.canRoundUp ? [] : ['up']}
                            renderLabel={renderChangeOptionLabel}
                            onChange={value => applyChangeOption(value as ChangeOption)}
                        />
                    )}
                    {/* Only editable with "Otro monto": the other options fill it */}
                    <PriceField
                        id="vuelto"
                        value={changeAmount}
                        min={0.01}
                        max={cashReceived}
                        disabled={changeMode !== 'custom'}
                        onChange={onChangeChange}
                    />
                </div>
            )
        })
    }
    // Rounding is only offered when every payment type allows it and there's a pending amount.
    // While giving change it's automatic (set by the change option) and validated when confirming
    if (!giveChange && (isRoundingEnabled || canRound)) {
        fields.push({
            name: 'rounding',
            type: FieldType.BOOLEAN,
            label: 'Redondear',
            render: () => (
                <BooleanField
                    id="roundingEnabled"
                    label="Redondear saldo pendiente"
                    value={isRoundingEnabled}
                    hint={`Cubre el saldo pendiente sin cobrarlo, hasta ${formatValue(minMaxRoundingAmount, FieldType.PRICE)}`}
                    optional
                    onChange={onToggleRounding}
                />
            )
        })
    }
    if (nonRoundingItems.length > 0) {
        const roundingAmount = roundingItem?.amount || 0
        const summaryParts = [
            `Recibido ${formatValue(round(nonRoundingPaidAmount - (giveChange ? changeAmount || 0 : 0)), FieldType.PRICE)}`,
            roundingAmount > 0 ? `Redondeo ${formatValue(roundingAmount, FieldType.PRICE)}` : undefined,
            giveChange ? `Vuelto ${formatValue(changeAmount || 0, FieldType.PRICE)}` : undefined,
            pendingAmount < 0 ? `Crédito a favor ${formatValue(Math.abs(pendingAmount), FieldType.PRICE)}` : undefined,
            pendingAmount > 0 ? `Pendiente ${formatValue(pendingAmount, FieldType.PRICE)}` : undefined
        ]
        fields.push({
            name: 'summary',
            type: FieldType.TEXT,
            label: 'Resumen',
            render: () => <span className="fw-bold">{defined(summaryParts).join(' · ')}</span>
        })
    }

    const validate = async (): Promise<string[]> => {
        const validations = !creditCoversPayment && nonRoundingItems.length === 0 ? toList('Debe agregar al menos 1 medio de pago') : []
        // Giving change, the rounding is automatic: the user is told what can be rounded
        if (validations.length === 0 && giveChange && roundingItem) {
            if (!allAllowRounding) {
                validations.push('El vuelto deja saldo pendiente y algún medio de pago cargado no permite redondear. Elegí otro vuelto.')
            } else if (roundingItem.amount > minMaxRoundingAmount) {
                validations.push(`Se puede redondear hasta ${formatValue(minMaxRoundingAmount, FieldType.PRICE)}. Elegí un vuelto menor.`)
            }
        }
        if (validations.length === 0 && !giveChange && roundingItem && (roundingItem.amount <= 0 || roundingItem.amount > effectiveRoundingCap)) {
            validations.push(`El redondeo debe ser mayor a $0,00 y no superar ${formatValue(effectiveRoundingCap, FieldType.PRICE)}.`)
        }
        if (validations.length === 0 && giveChange) {
            // A change below the overpaid amount is allowed: the rest goes to the customer credit in favor
            const change = changeAmount || 0
            change <= 0 && validations.push('El vuelto debe ser mayor a $0,00.')
            change > cashReceived && validations.push(`El vuelto no puede superar el efectivo recibido (${formatValue(cashReceived, FieldType.PRICE)}).`)
        }
        if (validations.length === 0) {
            const hasPendingAmount = pendingAmount > 0
            hasPendingAmount && validations.push('El saldo pendiente debe ser $0,00.')
        }
        if (validations.length === 0) {
            const paymentStatusValidation = await validatePaymentStatus(table, operation.id, paymentStatus)
            setPaymentStatusChanged(!!paymentStatusValidation)
            paymentStatusValidation && validations.push(paymentStatusValidation)
        }
        return validations
    }

    // El cobro según el vuelto y, en acopios, la ampliación y la entrega pendiente
    const submitPaymentData = async (data: Data2) => {
        let itemsToSubmit = salePaymentItems
        if (giveChange && changeAmount) {
            // The change is taken out of the cash received (validate() ensures it's enough)
            let remaining = changeAmount
            itemsToSubmit = salePaymentItems
                .map(item => {
                    if (remaining <= 0 || !isCashItem(item)) return item
                    const deduction = Math.min(item.amount, remaining)
                    remaining = round(remaining - deduction)
                    return { ...item, amount: round(item.amount - deduction) }
                })
                .filter(item => item.amount > 0)
        }
        await submitPayment(table, operation.id, data.paymentDate! as Date, itemsToSubmit, increaseAmount, collectIncreaseOnly ? increaseAmount : undefined, pendingDelivery)
    }

    const onSubmit = async (data: Data2) => {
        if (paymentTypeYieldChanged) {
            // El cobro no se bloquea: se advierte y se continúa con "Continuar".
            closeModal(ModalId.SPINNER)
            setPendingPaymentData(data)
            setModalValidations([yieldWarningMessage])
            setTimeout(() => openModal(ModalId.VALIDATION), 500)
            return
        }
        await submitPaymentData(data)
    }

    const onContinueWithYieldWarning = async () => {
        const data = pendingPaymentData
        setPendingPaymentData(undefined)
        if (!data) return
        Emitter.emit(Event.DB_SUBMIT)
        await submitPaymentData(data)
    }

    const onCancel = () => navigate(-1)

    const onSaveSalePaymentItem = (newPartialItem: Partial<SalePaymentItemData>) => {
        const newPartialSalePaymentItems = currentSalePaymentItem ?
            salePaymentItems.map(item => item.index === currentSalePaymentItem.index ? { ...newPartialItem, index: item.index } : item) :
            [newPartialItem, ...salePaymentItems]
        const newSalePaymentItems = sortItems(newPartialSalePaymentItems)
        setSalePaymentItems(newSalePaymentItems)
        // When the modal is closed, the keyboard continues after the "Agregar" button (e.g. "Dar vuelto" or "Confirmar")
        setTimeout(() => focusAfter(document.querySelector(ADD_PAYMENT_ITEM_SELECTOR)), 500)
    }

    const onGoBack = () => navigate(-1)

    return (
        <>
            <div ref={keyboardContainerRef} onKeyDown={onFormKeyDown}>
                <DataTypeForm2
                    formId="confirm-sale-payment-form"
                    fields={fields}
                    createLabel='Confirmar'
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    validate={validate}
                />
            </div>
            <SalePaymentItemFormModal
                salePaymentItem={currentSalePaymentItem}
                paymentTypes={paymentTypes}
                defaultPaymentTypeId={operation.paymentTypeId}
                defaultAmounts={pendingAmounts}
                defaultCheckName={customer.name}
                maxAmount={currentSalePaymentItem?.isRounding ? effectiveRoundingCap : undefined}
                roundingMode={currentSalePaymentItem?.isRounding}
                onSubmit={onSaveSalePaymentItem}
            />
            <ValidationModal2
                title="Validaciones"
                submitLabel={paymentStatusChanged ? "Volver" : pendingPaymentData ? "Continuar" : undefined}
                closeable={!paymentStatusChanged}
                onSubmit={paymentStatusChanged ? onGoBack : pendingPaymentData ? onContinueWithYieldWarning : undefined}
                onCancel={() => setPendingPaymentData(undefined)}
            />
        </>
    )
}
